'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import Image from 'next/image';
import { MapPin, Building, X, Mail, Phone, Star, Award } from 'lucide-react';
import { FaInstagram, FaLinkedinIn } from 'react-icons/fa';
import { BiLogoTiktok } from 'react-icons/bi';
import { useLanguage } from '@/app/context/language-context';
import AgentContactModal from '@/app/components/contact/agent-contact-modal-pixxi';
import { AgentBadges } from './agent-badge';

export default function Agent3DCard({ agent, index }) {
  const { t } = useLanguage();
  const [isHovered, setIsHovered] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  // Check if agent has any badges
  const hasBadges = agent?.is_super_agent || agent?.is_trubroker;

  return (
    <>
      <div
        className='relative bg-black rounded-xl overflow-hidden h-[450px] perspective-1000'
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Agent Promotion Badges */}
        {
          <div className='absolute top-4 left-4 z-30'>
            <AgentBadges agent={agent} />
          </div>
        }

        {/* Agent Image */}
        <div className='relative h-full w-full overflow-hidden'>
          <motion.div
            className='w-full h-full relative'
            animate={{ scale: isHovered ? 1.05 : 1 }}
            transition={{ duration: 0.5 }}
          >
            <Image
              src={agent?.profile_image?.trim() ||
                '/placeholder.svg?height=450&width=450&query=professional agent'
              }
              alt={agent.name}
              width={450}
              height={450}
              sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
              className='object-cover'
              priority
              unoptimized
            />
          </motion.div>

          {/* Overlay gradient */}
          <div className='absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent' />

          {/* Social media icons at top right */}
          <motion.div
            className='absolute top-4 right-4 flex space-x-2 z-20'
            initial={{ opacity: 0 }}
            animate={{ opacity: isHovered ? 1 : 0 }}
            transition={{ duration: 0.3 }}
          >
            {agent.socialLinks?.instagram && (
              <motion.a
                href={agent.socialLinks.instagram}
                target='_blank'
                rel='noopener noreferrer'
                className='p-2 rounded-full bg-white/20 backdrop-blur-sm text-white transition-all hover:bg-[#E4405F] hover:scale-110'
                aria-label='Instagram'
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaInstagram className='w-4 h-4' />
              </motion.a>
            )}
            {agent.socialLinks?.linkedin && (
              <motion.a
                href={agent.socialLinks.linkedin}
                target='_blank'
                rel='noopener noreferrer'
                className='p-2 rounded-full bg-white/20 backdrop-blur-sm text-white transition-all hover:bg-[#0077B5] hover:scale-110'
                aria-label='LinkedIn'
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaLinkedinIn className='w-4 h-4' />
              </motion.a>
            )}
            {agent.socialLinks?.twitter && (
              <motion.a
                href={agent.socialLinks.twitter}
                target='_blank'
                rel='noopener noreferrer'
                aria-label='Twitter'
                className='p-2 rounded-full bg-white/20 backdrop-blur-sm text-white transition-all hover:bg-black hover:scale-110'
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <X className='w-4 h-4' />
              </motion.a>
            )}
            {agent.socialLinks?.tiktok && (
              <motion.a
                href={agent.socialLinks.tiktok}
                target='_blank'
                rel='noopener noreferrer'
                aria-label='TikTok'
                className='p-2 rounded-full bg-white/20 backdrop-blur-sm text-white transition-all hover:bg-[#000000] hover:text-[#00f2ea] hover:scale-110'
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <BiLogoTiktok className='w-4 h-4' />
              </motion.a>
            )}
          </motion.div>

          {/* Agent info overlay */}
          <div className='absolute bottom-0 left-0 right-0 p-6 text-white'>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <h3 className='text-2xl font-bold mb-1'>{agent.name}</h3>
              <p className='text-secondarycolor mb-2 font-medium'>
                {agent.designation}
              </p>
            </motion.div>

            <motion.div
              className='space-y-1'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {agent.location && (
                <div className='flex items-center text-sm text-gray-300'>
                  <MapPin className='w-4 h-4 mr-2 text-secondarycolor' />
                  <span>{agent.location}</span>
                </div>
              )}

              <div className='flex items-center text-sm text-gray-300'>
                <Building className='w-4 h-4 mr-2 text-secondarycolor' />
                <span>Experience: {agent.experience} years</span>
              </div>

              {agent.languages && agent.languages.length > 0 && (
                <div className='flex items-start text-sm text-gray-300'>
                  <span className='mr-2 mt-0.5 text-secondarycolor'>
                    Languages:
                  </span>
                  <span className='flex-1 mt-0.5'>{agent.languages.join(', ')}</span>
                </div>
              )}
            </motion.div>

            {/* Contact info - appears on hover */}
            <motion.div
              className='mt-4 space-y-2'
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: isHovered ? 1 : 0, y: isHovered ? 0 : 10 }}
              transition={{ duration: 0.3 }}
            >
              {agent.email && (
                <motion.a
                  href={`mailto:${agent.email}`}
                  className='flex items-center text-gray-300 hover:text-secondarycolor transition-colors group'
                  whileHover={{ x: 5 }}
                >
                  <Mail className='w-4 h-4 mr-2 text-secondarycolor group-hover:scale-110 transition-transform' />
                  <span className='text-sm truncate'>{agent.email}</span>
                </motion.a>
              )}

              {agent.mobile && (
                <motion.a
                  href={`tel:${agent.mobile}`}
                  className='flex items-center text-gray-300 hover:text-secondarycolor transition-colors group'
                  whileHover={{ x: 5 }}
                >
                  <Phone className='w-4 h-4 mr-2 text-secondarycolor group-hover:scale-110 transition-transform' />
                  <span className='text-sm'>{agent.mobile}</span>
                </motion.a>
              )}

              {/* Contact Now button */}
              <motion.button
                onClick={() => setIsContactModalOpen(true)}
                className='mt-4 w-full py-2.5 bg-primarycolor text-black hover:bg-secondarycolor hover:text-black rounded-md font-medium flex items-center justify-center transition-all duration-300 shadow-lg'
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.98 }}
              >
                {t('contactNow') || 'Contact Now'}
              </motion.button>
            </motion.div>
          </div>
        </div>

        {/* Hover overlay effect */}
        <motion.div
          className='absolute inset-0 bg-gradient-to-t from-primarycolor/10 via-transparent to-transparent pointer-events-none'
          initial={{ opacity: 0 }}
          animate={{ opacity: isHovered ? 1 : 0 }}
          transition={{ duration: 0.3 }}
        />
      </div>

      {/* Agent Contact Modal */}
      <AgentContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        agent={agent}
      />
    </>
  );
}
